import React, {forwardRef, useImperativeHandle, useRef, useState} from "react";
import {Col} from "reactstrap";
import {UploadStates, WarrantyAndReturnMessageTypes,} from "../../../../../../../../core/constants/enums";
import Divider from "@mui/material/Divider";
import ChatSection from "../../../../../../../components/app-specific/part-support/part-support-chat/chat-section";
import ChatFooter from "../../../../../../../components/app-specific/part-support/part-support-chat/chat-footer";
import {Paper} from "@mui/material";
import Api from "../../../../../../../../core/services/api/api";
import {createUUId, dateComparator, numComparator, readFile, ReadingFileAs} from "../../../../../../../../core/services/utils/utils";
import Utils from "../../../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import TimeBubble from "../../../../../../../components/app-specific/part-support/part-support-chat/chat_bubble/time_bubble";
import TextBubbles from "../../../../../../../components/app-specific/part-support/part-support-chat/chat_bubble/text_bubble";
import ImageBubble from "../../../../../../../components/app-specific/part-support/part-support-chat/chat_bubble/image_bubble";
import VendorInformationModal from "../../../../../../../components/dialogs/vendor-information";
import FileBubble from "../../../../../../../components/app-specific/part-support/part-support-chat/chat_bubble/file_bubble";
import PartSupportRequestInfoTable from "../../../../../../../components/app-specific/part-support/part-support-request-info/table";
import {PartSupportRequestsDataGridCellKeys} from "../../../../../../../../core/constants/table/data-grid-column-keys";
import DateUtils from "../../../../../../../../core/services/date-utils";
import useAccessLevels from "../../../../../../../hooks/use-access-levels";
import ElementIds from "../../../../../../../../core/models/element-ids";
import {ChatMessageOperationTypes} from "../../index";


/**
 * Compares the messages based on their order index and submitted date time.
 * @param a {{orderIndex: number, submittedDateTime: Date, msgType: {id:number}}} message
 * @param b {{orderIndex: number, submittedDateTime: Date, msgType: {id:number}}} message
 * @return {number}
 */
const messageComparator = (a, b) => {
    let res = dateComparator(a.submittedDateTime, b.submittedDateTime);
    if (res === 0) {
        res = numComparator(a.orderIndex, b.orderIndex);
    }
    if (res === 0) {
        res = numComparator(b.msgType?.id, a.msgType?.id); // first 3 => Time then 1 => Text
    }
    return res;
}

const DataGridSaveConfig = {
    name: "PartSupportRequestInformationViewVendorTabDataGrid",
    version: 1,
}

const WarrantyAndReturnSingleVendorTabChat = forwardRef(({
                                                             makes,
                                                             models,
                                                             loading,
                                                             chats,
                                                             setChats,
                                                             request,
                                                             scrollInCaseOfChatChange,
                                                             setRequest,
                                                             height,
                                                             statuses,
                                                             setRequestProperty,
                                                         }, ref) => {
    const operatorAccessLevels = useAccessLevels();
    const [sending, setSending] = useState(false);
    const isMounted = useIsMounted();
    const headerRef = useRef(), footerRef = useRef(), bodyRef = useRef();
    const latestOrderIndex = Math.max(...chats?.map(e => e.orderIndex ?? -1), 0);
    const [openVendorInfo, setOpenVendorInfo] = useState(false);

    /**
     * Initializes the given ref with the necessary getters that binds the other refs to itself.
     */
    useImperativeHandle(ref, () => ({
        get headerRef() {
            return headerRef.current;
        },
        get footerRef() {
            return footerRef.current;
        },
        get bodyRef() {
            return bodyRef.current;
        },
    }), [headerRef, footerRef, bodyRef]);

    /**
     * For each of the items in the data, sends a new message.
     * @param messages {any[]}
     */
    const sendNewMessages = async (messages) => {
        setSending(true);
        const promises = [];
        for (const message of messages) {
            promises.push(sendNewMessage(message));
        }
        await Promise.allSettled(promises);
        if (!isMounted()) return
        setSending(false);
    }

    /**
     * Sends a new message for the operator to the user by api, and if the result of the api is successful, or
     * erroneous, appends to the list of chats, with their status.
     *
     * if the message is an image, also attaches the temporary url.
     * Also attaches the progress status of the message at the beginning and end for ui reflections.
     * @param data {any}
     */
    const sendNewMessage = async (data) => {
        data = {
            ...data,
            uploadState: {state: UploadStates.pending},
        };
        if (data.msgType?.id === WarrantyAndReturnMessageTypes.image) {
            data['tempUrl'] = await readFile(data.data, ReadingFileAs.dataUrl);
        }
        if ([
            WarrantyAndReturnMessageTypes.image,
            WarrantyAndReturnMessageTypes.pdf,
            WarrantyAndReturnMessageTypes.video,
            WarrantyAndReturnMessageTypes.zip,
        ].includes(data.msgType?.id)) {
            data['fileName'] = Utils.sanitizeFileName(data.data.name)
            data['data'] = btoa(await readFile(data.data, ReadingFileAs.binaryString));
        }
        setChats(prevState => [...prevState, data]);
        setTimeout(() => {
            if (!isMounted()) return;
            scrollInCaseOfChatChange(data, ChatMessageOperationTypes.add);
        }, 500)
        await sendWarrantyAndReturnMessage(data);
    }

    /**
     * Resends a message that is failed to be sent for the operator to the user by api, and if the result of the api is
     * successful, or erroneous, appends to the list of chats, with their status.
     *
     * Resets the progress status of the message at the beginning and end for ui reflections.
     * @param data {any}
     * @param setLoading {function} the function to set the state to loading
     */
    const resendMessage = async (data, setLoading) => {
        setLoading(true);
        setChats(prevState => prevState.map(e => e._reactKey === data._reactKey
            && e.id === data.id
                ? {
                    ...data,
                    uploadState: {state: UploadStates.pending}
                }
                : e
        ));
        setTimeout(() => {
            if (!isMounted()) return;
            scrollInCaseOfChatChange(data, ChatMessageOperationTypes.add);
        }, 0)
        await sendWarrantyAndReturnMessage(data, true);
        setLoading(false);
    }

    /**
     * Calls api for sending a new Message and then if the response returns the result.
     * @param data {any}
     * @param resending {boolean}
     */
    const sendWarrantyAndReturnMessage = async (data, resending) => {
        const forApi = {
            partSupportReqId: request.id,
            msgTypeId: data.msgType?.id,
            ...(([
                    WarrantyAndReturnMessageTypes.image,
                    WarrantyAndReturnMessageTypes.pdf,
                    WarrantyAndReturnMessageTypes.video,
                    WarrantyAndReturnMessageTypes.zip,
                ].includes(data.msgType?.id))
                    ? {
                        content: data.data,
                        fileName: data.fileName,
                    }
                    : {
                        textMsg: data.data,
                    }
            ),
        };
        const response = await Api.sendWarrantyAndReturnMessageToVendor(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setChats(prevState => prevState.map(e => e._reactKey === data._reactKey
                ? {
                    ...e,
                    ...response?.data,
                    _reactKey: resending ? `_${createUUId()}` : data._reactKey,
                    submittedDateTime: DateUtils.UTCToServer(DateUtils.UTC(response.data.submittedDateTime)).format(DateUtils.withoutOffsetFormat),
                    uploadState: {state: UploadStates.done}
                }
                : e
            ));
        } else {
            setChats(prevState => prevState.map(e => e._reactKey === data._reactKey
                ? {
                    ...e,
                    uploadState: {state: UploadStates.error}
                }
                : e
            ));
        }
    }

    /**
     * Removes a message from the chat list by calling the api and if the result of the api is successful then
     * removes them from the ui.
     * @param message {any} the message to be removed
     * @param setLoading {function} to set the loading state
     */
    const removeMessage = (message, setLoading) => {
        setLoading(true);
        Api.removeWarrantyAndReturnMessageFromVendor(message.id).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                scrollInCaseOfChatChange(message, ChatMessageOperationTypes.remove);
                setChats(prevState => prevState?.filter((e) => e.id !== message.id));
            }
            setLoading(false);
        })
    }

    /**
     * Creates a chat object for a new message in chat section
     * @param {any} e
     * @param {number} index
     * @param {number} type
     * @return {{_reactKey: string, msgType: {id: number}, data: *, vendor: null, submittedDateTime: string, isForwardedMsg: boolean, orderIndex: number, id: `new-message-${number}`, isActive: boolean, operator: {}, forwardedMsgInfo: null}}
     */
    const createChatObject = (e, type, index) => ({
        id: `new-message-${latestOrderIndex + 1 + index}`,
        data: e,
        submittedDateTime: DateUtils.serverNow(),
        orderIndex: latestOrderIndex + 1 + index,
        vendor: null,
        operator: {},
        isActive: true,
        forwardedMsgInfo: null,
        isForwardedMsg: false,
        _reactKey: `_${createUUId()}`,
        msgType: {
            id: type,
        },
    })

    /**
     * Creates the time object for the chat section
     * @param {string} time
     * @return {{data: (null|string|*), typeID: number, submitDateTime: (null|string|*)}}
     */
    const createChatSectionTimeObject = (time) => ({
        id: time,
        data: time,
        submittedDateTime: time,
        orderIndex: -1,
        vendor: null,
        operator: {},
        isActive: true,
        forwardedMsgInfo: null,
        isForwardedMsg: false,
        msgType: {
            id: WarrantyAndReturnMessageTypes.time,
            name: "Time"
        },
    });

    /**
     * Creates the bubble components for the chat section
     * @param data
     * @return {null|{component: JSX.Element, key: string}}
     */
    const createBubbleComponent = (data) => {
        switch (data.msgType.id) {
            case WarrantyAndReturnMessageTypes.time:
                return {
                    key: data.submittedDateTime,
                    component: <TimeBubble
                        data={data}
                    />
                };
            case WarrantyAndReturnMessageTypes.forwardedMessage:
                switch (data.forwardedMsgInfo?.typeID) {
                    case WarrantyAndReturnMessageTypes.text:
                        return {
                            key: data._reactKey,
                            component: <TextBubbles
                                data={data}
                                removeMessage={removeMessage}
                                sendAgain={resendMessage}
                            />,
                        }
                    case WarrantyAndReturnMessageTypes.image:
                        return {
                            key: data._reactKey,
                            component: <ImageBubble
                                data={data}
                                removeMessage={removeMessage}
                                sendAgain={resendMessage}
                            />,
                        }
                    case WarrantyAndReturnMessageTypes.pdf:
                    case WarrantyAndReturnMessageTypes.video:
                    case WarrantyAndReturnMessageTypes.zip:
                        return {
                            key: data._reactKey,
                            component: <FileBubble
                                data={data}
                                typeId={data.forwardedMsgInfo.typeID}
                                removeMessage={removeMessage}
                                sendAgain={resendMessage}
                            />,
                        }
                    default:
                        return null;
                }
            case WarrantyAndReturnMessageTypes.text:
                return {
                    key: data._reactKey,
                    component: <TextBubbles
                        data={data}
                        removeMessage={removeMessage}
                        sendAgain={resendMessage}
                    />,
                }
            case WarrantyAndReturnMessageTypes.image:
                return {
                    key: data._reactKey,
                    component: <ImageBubble
                        data={data}
                        removeMessage={removeMessage}
                        sendAgain={resendMessage}
                    />,
                }
            case WarrantyAndReturnMessageTypes.pdf:
            case WarrantyAndReturnMessageTypes.video:
            case WarrantyAndReturnMessageTypes.zip:
                return {
                    key: data._reactKey,
                    component: <FileBubble
                        data={data}
                        typeId={data.msgType?.id}
                        removeMessage={removeMessage}
                        sendAgain={resendMessage}
                    />,
                }
            default:
                return null;
        }
    }


    return (
        <>
            <Paper className={`paper w-100 d-flex flex-column`}>
                <div
                    ref={headerRef}
                    id={ElementIds.partSupportChat.header}
                    className='row no-gutters align-items-center justify-content-between w-100'
                >
                    <PartSupportRequestInfoTable
                        makes={makes}
                        models={models}
                        data={!!Object.keys(request).length ? [request] : [{id: 1}]}
                        disableInteractions={loading}
                        setRequestProperty={setRequestProperty}
                        statuses={statuses}
                        exclude={[
                            PartSupportRequestsDataGridCellKeys.print
                        ]}
                        tableProps={{
                            saveConfig: DataGridSaveConfig,
                            state: {
                                loading: {state: loading, count: 1},
                            },
                            layout: {
                                hideFooter: true,
                                hideToolbar: true,
                            },
                            misc: {
                                visibleRows: 1,
                            },
                        }}
                    />
                    <Divider className='divider'/>
                </div>
                <div
                    ref={bodyRef}
                    id={ElementIds.partSupportChat.body}
                    className='row no-gutters align-items-center justify-content-center w-100'
                >
                    <Col xs={12} className='transition' style={{height: `${height}px`}}>
                        {
                            loading
                                ? <div className='w-100 loading chat mb-3 p-3'>
                                    <div/>
                                </div>
                                : operatorAccessLevels.packages.warrantyAndReturn.chatWithVendor &&
                                <ChatSection
                                    send={sendNewMessages}
                                    sending={sending}
                                    chats={chats}
                                    createBubbleComponent={createBubbleComponent}
                                    messageComparator={messageComparator}
                                    createObject={createChatObject}
                                    createTime={createChatSectionTimeObject}
                                />

                        }
                    </Col>
                </div>
                <div
                    ref={footerRef}
                    id={ElementIds.partSupportChat.footer}
                    className='row no-gutters align-items-start justify-content-between w-100'
                >
                    <Divider className='divider w-100'/>
                    <div className='w-100 my-2'/>
                    {
                        operatorAccessLevels.packages.warrantyAndReturn.chatWithVendor &&
                        <ChatFooter
                            sending={sending}
                            send={sendNewMessages}
                            chatsLength={chats?.length ?? 0}
                            createObject={createChatObject}
                        />
                    }
                </div>
            </Paper>
            <VendorInformationModal
                vendor={request?.vendor}
                vendorAccessLink={request?.vendorAccessLink}
                open={openVendorInfo}
                setOpen={setOpenVendorInfo}
                setNewLink={(newLink) => setRequest(prevState => ({...prevState, vendorAccessLink: newLink}))}
            />
        </>
    );
});


export default WarrantyAndReturnSingleVendorTabChat;

